/**
 * variant-gallery-sync.js
 *
 * Adds bidirectional sync between the product gallery and variant picker:
 *
 *   GALLERY -> VARIANT
 *     Clicking any thumbnail updates the variant picker to match,
 *     exactly as if the customer had clicked the dropdown directly.
 *     Generic/shared images (not tied to a variant) are ignored.
 *
 *   VARIANT -> GALLERY (suppressed)
 *     Dawn's native behavior reorders the gallery to put the newly
 *     selected variant's image first. Since the customer just clicked
 *     that image directly, it's already in view — reordering only
 *     disrupts their scroll position. We let Dawn's update happen
 *     (for price/SKU/inventory/etc), then restore the gallery's
 *     thumbnail and slide order to what it was right before the click.
 */

(function () {
  class VariantGallerySync {
    constructor(productInfoSection) {
      this.section = productInfoSection;
      this.productUrl = productInfoSection.dataset.url;

      if (!this.productUrl) return;

      this.productData = null;
      this.mediaIdToVariants = new Map();

      // Capture the TRUE original order synchronously, right now, from
      // whatever is already rendered in the DOM. This must not wait on
      // the async product-data fetch below — if the customer's first
      // interaction happens before that fetch resolves, Dawn's native
      // reorder would already have run unopposed, and capturing order
      // afterward would lock in that wrong position as "original"
      // forever. DOM order doesn't depend on product data, so there's
      // no reason to wait.
      this.originalOrder = this.captureOrder();

      // Likewise, the dropdown/swatch listener only needs this.section
      // and this.originalOrder, both already available — bind it now
      // so the very first customer interaction is covered, not just
      // the second and later ones.
      this.bindDirectVariantChanges();

      // Thumbnail clicks DO need product data (to map a clicked image
      // back to a variant), so that listener waits for the fetch.
      this.fetchProductData().then(() => {
        this.buildMediaMap();
        this.bindThumbnailClicks();
      });
    }

    async fetchProductData() {
      try {
        const response = await fetch(`${this.productUrl}.js`);
        this.productData = await response.json();
      } catch (err) {
        console.error('variant-gallery-sync: failed to fetch product JSON', err);
      }
    }

    buildMediaMap() {
      if (!this.productData) return;
      this.productData.variants.forEach((variant) => {
        if (!variant.featured_media) return;
        const mediaId = variant.featured_media.id;
        if (!this.mediaIdToVariants.has(mediaId)) {
          this.mediaIdToVariants.set(mediaId, []);
        }
        this.mediaIdToVariants.get(mediaId).push(variant);
      });
    }

    bindThumbnailClicks() {
      this.section.addEventListener('click', (event) => {
        const thumbnailBtn = event.target.closest('.thumbnail-list__item button');
        if (!thumbnailBtn) return;

        const item = thumbnailBtn.closest('.thumbnail-list__item');
        if (!item) return;

        const fullMediaId = item.dataset.target || '';
        const mediaId = Number(fullMediaId.slice(fullMediaId.lastIndexOf('-') + 1));
        if (!mediaId) return;

        const matchingVariants = this.mediaIdToVariants.get(mediaId);
        if (!matchingVariants || matchingVariants.length === 0) return;

        const variant = this.pickBestVariant(matchingVariants);
        if (!variant) return;

        // Always restore back toward the TRUE original page-load order,
        // not whatever the strip happens to look like right now. A
        // per-click "current state" snapshot risks locking in a wrong
        // position from an earlier interaction and preserving it forever.
        this.selectVariant(variant, this.originalOrder);
      });
    }

    pickBestVariant(variants) {
      return variants.find((v) => v.available) || variants[0];
    }

    // Listens for the customer interacting with the picker directly
    // (dropdown change, swatch/button click) — distinct from our own
    // synthetic changes fired by bindThumbnailClicks. Captures the
    // gallery's order/scroll BEFORE Dawn's own VariantSelects handler
    // kicks off its fetch-and-reorder, then restores it afterward,
    // for the same reason we do this on thumbnail clicks: the customer
    // hasn't asked for the strip to jump around, just for the variant
    // to update.
    bindDirectVariantChanges() {
      const variantSelects = this.section.querySelector('variant-selects');
      if (!variantSelects) return;

      variantSelects.addEventListener('change', () => {
        if (this.isSyncDriven) return; // this is one of our own synthetic changes; bindThumbnailClicks already handles its own snapshot/restore
        this.pollAndRestore(this.originalOrder);
      });
    }

    captureOrder() {
      const slideList = this.section.querySelector('.product__media-list');
      const thumbList = this.section.querySelector('.thumbnail-list');
      return {
        slideIds: slideList ? Array.from(slideList.children).map((el) => el.id) : [],
        thumbIds: thumbList ? Array.from(thumbList.children).map((el) => el.id) : [],
        slideScrollLeft: slideList ? slideList.scrollLeft : 0,
        thumbScrollLeft: thumbList ? thumbList.scrollLeft : 0,
      };
    }

    restoreOrder(snapshot) {
      const slideList = this.section.querySelector('.product__media-list');
      const thumbList = this.section.querySelector('.thumbnail-list');

      if (slideList && snapshot.slideIds.length) {
        const byId = new Map(Array.from(slideList.children).map((el) => [el.id, el]));
        snapshot.slideIds.forEach((id) => {
          const el = byId.get(id);
          if (el) slideList.appendChild(el);
        });
        slideList.scrollLeft = snapshot.slideScrollLeft;
      }

      if (thumbList && snapshot.thumbIds.length) {
        const byId = new Map(Array.from(thumbList.children).map((el) => [el.id, el]));
        snapshot.thumbIds.forEach((id) => {
          const el = byId.get(id);
          if (el) thumbList.appendChild(el);
        });
        thumbList.scrollLeft = snapshot.thumbScrollLeft;
      }
    }

    selectVariant(variant, snapshot) {
      const variantSelects = this.section.querySelector('variant-selects');
      if (!variantSelects) return;

      let changedAny = false;
      this.isSyncDriven = true;

      this.productData.options.forEach((option, index) => {
        const targetValue = variant.options[index];
        if (targetValue == null) return;

        const escapedValue = CSS.escape(targetValue);

        const optionElement = variantSelects.querySelector(
          `select option[value="${escapedValue}"]`
        );
        if (optionElement) {
          const select = optionElement.closest('select');
          if (select && select.value !== targetValue) {
            select.value = targetValue;
            changedAny = true;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }

        const input = variantSelects.querySelector(
          `fieldset input[type="radio"][value="${escapedValue}"]`
        );
        if (input && !input.checked) {
          input.checked = true;
          changedAny = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      // Clear the flag on next tick - after this event's listeners
      // (including our own bindDirectVariantChanges one) have run.
      window.setTimeout(() => {
        this.isSyncDriven = false;
      }, 0);

      if (changedAny) {
        this.pollAndRestore(snapshot);
      }
    }

    pollAndRestore(snapshot) {
      // If a previous selection's poll/restore is still in flight,
      // cancel it first. Letting two cycles run concurrently against
      // the same DOM causes them to step on each other — one cycle's
      // restore can yank elements out from under another's in-progress
      // read, which is exactly the "missing main image" / "reverted to
      // default reorder" failure mode this guards against.
      if (this.restoreTimeoutId) {
        window.clearTimeout(this.restoreTimeoutId);
        this.restoreTimeoutId = null;
      }

      // Dawn's fetch + DOM update is async. Poll briefly for the new
      // media list to settle, then restore the pre-change order.
      // 50ms steps, up to ~1.5s — generous enough for the round trip
      // without leaving a visible "snap back" if it resolves sooner.
      let attempts = 0;
      const maxAttempts = 30;
      const tryRestore = () => {
        attempts += 1;
        const slideList = this.section.querySelector('.product__media-list');
        const orderChanged =
          slideList &&
          Array.from(slideList.children)[0]?.id !== snapshot.slideIds[0];

        if (orderChanged) {
          this.restoreOrder(snapshot);
          this.restoreTimeoutId = null;
          return;
        }
        if (attempts < maxAttempts) {
          this.restoreTimeoutId = window.setTimeout(tryRestore, 50);
        } else {
          this.restoreTimeoutId = null;
        }
      };
      this.restoreTimeoutId = window.setTimeout(tryRestore, 50);
    }
  }

  function initAll() {
    document.querySelectorAll('product-info').forEach((section) => {
      if (section.__variantGallerySync) return;
      section.__variantGallerySync = new VariantGallerySync(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();